<template>
  <v-dialog :model-value="modelValue" max-width="650" persistent @update:model-value="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Extrait de compte</span>
        <v-spacer />
        <v-btn icon variant="text" aria-label="Fermer" @click="close(false)">
          <v-icon>fas fa-xmark</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <p>Choisissez la période sur laquelle vous souhaitez exporter votre extrait de compte au format Excel</p>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field v-model="dateFrom" label="Du" type="date" variant="outlined" required />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="dateTo" label="Au" type="date" :min="dateFrom || undefined" variant="outlined" required />
          </v-col>
        </v-row>
        <div v-if="loading" class="d-flex align-center ga-3 py-2">
          <v-progress-circular indeterminate color="primary" size="24" />
          <span>Recherche des mouvements du {{ displayDate(dateFrom) }} au {{ displayDate(dateTo) }}…</span>
        </div>
        <p v-else-if="searched && !hasMovements" class="py-2">
          Oups, il semble qu'il n'y ait aucun mouvement sur votre compte sur la période sélectionnée.
          Peut-être devriez-vous rechercher sur une période plus large ?
        </p>
        <p v-else-if="exported" class="py-2">Fichier exporté !</p>
        <p v-else-if="error" class="text-error py-2">{{ error }}</p>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="primary" :disabled="loading || !hasMovements || !validPeriod" :loading="exporting" @click="exportStatement">
          Exporter
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import AccountEntry, { AccountStatementQuery } from '@/generated/classes/AccountEntry';
import Etablissement from '@/generated/classes/Etablissement';
import Files, { DownloadData } from '@/generated/classes/Files';
import { getBlob } from '@/framework/files/helpers/FileTypes';

export default defineComponent({
  props: {
    modelValue: { type: Boolean, required: true },
    etablissement: { type: Object as PropType<Etablissement>, required: true }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      dateFrom: '',
      dateTo: '',
      loading: false,
      exporting: false,
      searched: false,
      hasMovements: false,
      exported: false,
      error: '',
      searchVersion: 0
    };
  },
  computed: {
    validPeriod(): boolean {
      return this.dateFrom !== '' && this.dateTo !== '' && this.dateTo >= this.dateFrom;
    }
  },
  watch: {
    dateFrom() { this.search(); },
    dateTo() { this.search(); },
    modelValue(open: boolean) {
      if (open) this.initialiseCurrentYear();
      else this.reset();
    }
  },
  methods: {
    close(value: boolean): void {
      this.$emit('update:modelValue', value);
    },
    reset(): void {
      this.searchVersion++;
      this.dateFrom = '';
      this.dateTo = '';
      this.loading = false;
      this.exporting = false;
      this.searched = false;
      this.hasMovements = false;
      this.exported = false;
      this.error = '';
    },
    initialiseCurrentYear(): void {
      const year = new Date().getFullYear();
      this.dateFrom = `${year}-01-01`;
      this.dateTo = `${year}-12-31`;
    },
    createQuery(): AccountStatementQuery {
      const query = new AccountStatementQuery();
      query.etablissementId = this.etablissement.id;
      query.dateFrom = new Date(`${this.dateFrom}T12:00:00`);
      query.dateTo = new Date(`${this.dateTo}T12:00:00`);
      return query;
    },
    async search(): Promise<void> {
      const version = ++this.searchVersion;
      this.exported = false;
      this.error = '';
      this.searched = false;
      this.hasMovements = false;
      if (!this.validPeriod) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const [hasMovements] = await Promise.all([
          AccountEntry.hasMovements(this.createQuery()),
          new Promise(resolve => window.setTimeout(resolve, 3000))
        ]);
        if (version !== this.searchVersion) return;
        this.hasMovements = hasMovements;
        this.searched = true;
      } catch {
        if (version !== this.searchVersion) return;
        this.error = "La recherche des mouvements n'a pas pu aboutir.";
      } finally {
        if (version === this.searchVersion) this.loading = false;
      }
    },
    async exportStatement(): Promise<void> {
      if (!this.validPeriod || !this.hasMovements || this.exporting) return;
      this.exporting = true;
      this.error = '';
      try {
        const data = new DownloadData();
        data.service = 'AccountStatement';
        data.type.extension = 'xlsx';
        data.data = this.createQuery();
        const buffer = await Files.download(data);
        const blob = getBlob(buffer, data.type.extension);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const today = new Date();
        const exportDate = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
        const company = (this.etablissement.nom || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '');
        link.href = url;
        link.download = `EXTRAIT_DE_COMPTE_${this.etablissement.id}_${company}_${exportDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        this.searchVersion++;
        this.exported = true;
        this.searched = false;
        this.hasMovements = false;
      } catch {
        this.error = "L'export de l'extrait de compte n'a pas pu aboutir.";
      } finally {
        this.exporting = false;
      }
    },
    displayDate(value: string): string {
      if (!value) return '';
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    }
  }
});
</script>
